import type { CSSProperties, ReactNode } from "react";

export const BRAND_CYAN = "#69E2FC";

type BtnVariant = "primary" | "outline" | "ghost";
type BtnSize = "sm" | "md" | "lg";

const btnSizes: Record<BtnSize, CSSProperties> = {
  sm: { height: 32, padding: "0 12px", fontSize: 13 },
  md: { height: 40, padding: "0 16px", fontSize: 14 },
  lg: { height: 46, padding: "0 22px", fontSize: 15 },
};

const btnVariants: Record<BtnVariant, CSSProperties> = {
  primary: { background: BRAND_CYAN, color: "#062734" },
  outline: {
    background: "transparent",
    borderColor: "var(--border)",
    color: "var(--foreground)",
  },
  ghost: { background: "transparent", color: "var(--muted-foreground)" },
};

type BtnProps = {
  variant?: BtnVariant;
  size?: BtnSize;
  children: ReactNode;
  href?: string;
  onClick?: () => void;
  style?: CSSProperties;
  target?: string;
  rel?: string;
};

export const Btn = ({
  variant = "primary",
  size = "md",
  children,
  href,
  onClick,
  style,
  target,
  rel,
}: BtnProps) => {
  const merged: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    justifyContent: "center",
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    border: "1px solid transparent",
    borderRadius: 8,
    cursor: "pointer",
    transition:
      "background 120ms cubic-bezier(0.2,0.8,0.2,1), color 120ms cubic-bezier(0.2,0.8,0.2,1), border-color 120ms cubic-bezier(0.2,0.8,0.2,1)",
    whiteSpace: "nowrap",
    textDecoration: "none",
    ...btnSizes[size],
    ...btnVariants[variant],
    ...style,
  };
  if (href) {
    return (
      <a href={href} style={merged} target={target} rel={rel}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" style={merged} onClick={onClick}>
      {children}
    </button>
  );
};

export const Eyebrow = ({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontFamily: "var(--font-mono)",
      fontSize: 12,
      fontWeight: 500,
      color: "var(--muted-foreground)",
      letterSpacing: "0.04em",
      textTransform: "none",
      ...style,
    }}
  >
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: 99,
        background: BRAND_CYAN,
        display: "inline-block",
      }}
    />
    {children}
  </span>
);

export const Container = ({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) => (
  <div
    style={{
      maxWidth: 1200,
      margin: "0 auto",
      padding: "0 48px",
      ...style,
    }}
  >
    {children}
  </div>
);

export const SectionHead = ({
  eyebrow,
  heading,
  subhead,
  align = "left",
  maxWidth = 760,
}: {
  eyebrow?: ReactNode;
  heading: ReactNode;
  subhead?: ReactNode;
  align?: "left" | "center";
  maxWidth?: number;
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 18,
      alignItems: align === "center" ? "center" : "flex-start",
      textAlign: align,
      margin: align === "center" ? "0 auto" : undefined,
      maxWidth: align === "center" ? maxWidth : undefined,
    }}
  >
    {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
    <h2
      style={{
        font: "500 44px/52px 'Newsreader', Georgia, serif",
        letterSpacing: "-1.1px",
        margin: 0,
        maxWidth,
      }}
    >
      {heading}
    </h2>
    {subhead ? (
      <p
        style={{
          font: "400 18px/28px var(--font-sans)",
          color: "var(--muted-foreground)",
          margin: 0,
          maxWidth: 640,
        }}
      >
        {subhead}
      </p>
    ) : null}
  </div>
);
